import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { isInteger } from '@iconik-io/core/src/is_integer';

type Options = {
  // Number of requests when throttling activates.
  // Don't use too low values, 10 should be enough to keep requests
  // from breaking through the rate limiter and getting 429 errors.
  throttleThreshold: number;

  // Keep these parameters in sync with iconik gateway rate limiter.
  maxTokens: number;
  refillTime: number;
  refillAmount: number;
};

const HEADER_REMAINING = 'ratelimit-remaining';

const logger = DebugLogger.getInstance('axios_throttle');

class Throttle {
  limitPrediction: number;

  predictionIntervalId: number | undefined;

  options: Readonly<Options>;

  constructor(options: Readonly<Options>) {
    this.options = options;
    this.limitPrediction = options.maxTokens;
  }

  apply = (axios: AxiosInstance) => {
    axios.interceptors.request.use(this.handleRequest, this.handleError);
    axios.interceptors.response.use(this.handleResponse, this.handleError);
  };

  ensurePredictor = () => {
    if (this.predictionIntervalId) {
      return;
    }
    this.predictionIntervalId = window.setInterval(() => {
      this.limitPrediction += this.options.refillAmount;

      if (this.limitPrediction >= this.options.maxTokens) {
        this.limitPrediction = this.options.maxTokens;
        clearInterval(this.predictionIntervalId);
        this.predictionIntervalId = undefined;
      }

      logger.debug('predicted limit refilled', this.limitPrediction);
    }, this.options.refillTime * 1000);
  };

  postpone = (
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    logger.debug('postponed', config.url);
    const promise = new Promise<InternalAxiosRequestConfig>((resolve) => {
      setTimeout(() => {
        resolve(config);
      }, this.options.refillTime * 1000);
    });
    return promise.then(this.handleRequest);
  };

  handleError = (error: AxiosError) => Promise.reject(error);

  handleRequest = (
    config: InternalAxiosRequestConfig,
  ): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
    logger.debug('predicted limit', this.limitPrediction);
    this.ensurePredictor();

    if (this.limitPrediction < this.options.throttleThreshold) {
      return this.postpone(config);
    }
    this.limitPrediction -= 1;
    return config;
  };

  handleResponse = (response: AxiosResponse): AxiosResponse => {
    let remaining = Number(response.headers[HEADER_REMAINING]);
    if (!isInteger(remaining)) {
      return response;
    }
    if (remaining < 0) {
      remaining = 0;
    }
    this.limitPrediction = remaining;
    logger.debug('limit remaining', remaining);

    return response;
  };
}

export function throttle(
  axios: AxiosInstance,
  options: Options,
): AxiosInstance {
  const t = new Throttle(options);
  t.apply(axios);

  return axios;
}
